import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api";
import AdminLayout from "../components/AdminLayout";
import {
  Home, ArrowLeft, Phone, Mail, Building2, CalendarDays, Search,
  SlidersHorizontal, FileText, Clock, PenSquare, DollarSign,
  Image as ImageIcon, Mic, MessageCircle, ChevronLeft, ChevronRight,
} from "lucide-react";

const initials = (name) => (name?.[0] || "?").toUpperCase();
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }) : "";
const fmtMoney = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
    .format(Number(n) || 0);

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700", "bg-green-100 text-green-700", "bg-violet-100 text-violet-700",
  "bg-orange-100 text-orange-700", "bg-sky-100 text-sky-700", "bg-rose-100 text-rose-700",
];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// Request.status values the server actually sets
const STATUS_STYLES = {
  "Pending": "bg-amber-100 text-amber-700",
  "Quoted": "bg-blue-100 text-blue-700",
  "Approved": "bg-green-100 text-green-700",
  "Rejected": "bg-red-100 text-red-600",
};
const ALL_STATUSES = ["Pending", "Quoted", "Approved", "Rejected"];
const OPEN_STATUSES = ["Pending", "Quoted"];
const statusPill = (s) => STATUS_STYLES[s] || "bg-slate-100 text-slate-600";

const QUOTE_STYLES = {
  none: "bg-slate-100 text-slate-500",
  pending: "bg-slate-100 text-slate-500",
  draft: "bg-amber-100 text-amber-700",
  sent: "bg-blue-100 text-blue-700",
  approved: "bg-green-100 text-green-700",
  declined: "bg-red-100 text-red-600",
  revision_requested: "bg-purple-100 text-purple-700",
};

const PAGE_SIZE = 8;

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/customers/${id}`);
      setCustomer(data.customer || null);
      setStats(data.stats || null);
      setRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't load this customer.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const filtered = useMemo(() => {
    let list = requests;
    if (status !== "All") list = list.filter((r) => r.status === status);

    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.requestId, r.rego, r.vin, r.partRequired, r.partNumber, r.quoteId, r.notes]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return list;
  }, [requests, query, status]);

  useEffect(() => { setPage(1); }, [query, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const filterOptions = useMemo(() => {
    const present = new Set(requests.map((r) => r.status));
    return ["All", ...ALL_STATUSES.filter((s) => present.has(s))];
  }, [requests]);

  return (
    <AdminLayout>
      <main className="p-6">
        {/* header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/customers")}
              className="grid place-items-center w-9 h-9 rounded-lg border border-gray-200 bg-white text-slate-500 hover:bg-slate-50"
              aria-label="Back to customers"
            >
              <ArrowLeft size={16} />
            </button>
            <h1 className="text-3xl font-extrabold text-slate-900">
              {customer?.name || "Customer"}
            </h1>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-2 pl-12">
            <Home size={14} />
            <Link to="/admin/dashboard" className="hover:text-slate-600">Dashboard</Link>
            <span>›</span>
            <Link to="/admin/customers" className="hover:text-slate-600">Customers</Link>
            <span>›</span>
            <span className="text-slate-500">{customer?.name || "Profile"}</span>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-2xl shadow-sm py-16 text-center text-slate-400">
            Loading customer…
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl shadow-sm py-16 text-center">
            <p className="text-red-500 mb-3">{error}</p>
            <button
              onClick={load}
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && customer && (
          <>
            {/* profile + stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-4">
                  <span className={`grid place-items-center w-14 h-14 rounded-full text-lg font-bold shrink-0 ${avatarColor(customer.name)}`}>
                    {initials(customer.name)}
                  </span>
                  <div className="min-w-0">
                    <div className="font-bold text-lg text-slate-800 truncate">{customer.name}</div>
                    <div className="text-xs text-slate-400">
                      Customer since {fmtDate(customer.createdAt)}
                    </div>
                  </div>
                </div>

                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-center gap-2.5 text-slate-600">
                    <Mail size={15} className="text-slate-400 shrink-0" />
                    <a href={`mailto:${customer.email}`} className="truncate hover:text-slate-900">
                      {customer.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5 text-slate-600">
                    <Phone size={15} className="text-slate-400 shrink-0" />
                    {customer.mobile
                      ? <a href={`tel:${customer.mobile}`} className="hover:text-slate-900">{customer.mobile}</a>
                      : "—"}
                  </div>
                  <div className="flex items-center gap-2.5 text-slate-600">
                    <Building2 size={15} className="text-slate-400 shrink-0" />
                    {customer.abn ? `ABN: ${customer.abn}` : "No ABN on file"}
                  </div>
                  <div className="flex items-center gap-2.5 text-slate-600">
                    <CalendarDays size={15} className="text-slate-400 shrink-0" />
                    Last activity {fmtDate(customer.lastActivity)}
                  </div>
                </div>

                {stats?.unreadMessages > 0 && (
                  <button
                    onClick={() => navigate("/admin/comments")}
                    className="mt-5 w-full flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900"
                  >
                    <MessageCircle size={15} />
                    Read {stats.unreadMessages} new {stats.unreadMessages === 1 ? "message" : "messages"}
                  </button>
                )}
              </div>

              <div className="lg:col-span-2 grid grid-cols-2 xl:grid-cols-4 gap-5">
                <StatCard icon={FileText} label="Total requests" value={stats?.totalRequests ?? 0} tone="bg-blue-50 text-blue-600" />
                <StatCard icon={Clock} label="Open requests" value={requests.filter((r) => OPEN_STATUSES.includes(r.status)).length} tone="bg-orange-50 text-orange-600" />
                <StatCard icon={PenSquare} label="Quotes sent" value={stats?.quotesSent ?? 0} tone="bg-violet-50 text-violet-600" />
                <StatCard
                  icon={DollarSign}
                  label={`Approved value · ${stats?.approved ?? 0}`}
                  value={fmtMoney(stats?.approvedValue)}
                  tone="bg-green-50 text-green-600"
                />
              </div>
            </div>

            {/* requests */}
            <div className="bg-white rounded-2xl shadow-sm">
              <div className="flex items-center gap-3 p-5 border-b border-gray-100">
                <h2 className="text-base font-bold text-slate-800 shrink-0">Requests received</h2>

                <div className="flex-1 flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 max-w-sm ml-4">
                  <Search size={16} className="text-slate-400" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search request ID, rego, VIN, part..."
                    className="flex-1 text-sm focus:outline-none placeholder:text-slate-400"
                  />
                </div>

                <div className="relative ml-auto">
                  <button
                    onClick={() => setShowFilterMenu((s) => !s)}
                    className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <SlidersHorizontal size={15} className="text-slate-400" />
                    {status === "All" ? "Filters" : status}
                  </button>
                  {showFilterMenu && (
                    <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-10 w-52">
                      {filterOptions.map((s) => (
                        <button
                          key={s}
                          onClick={() => { setStatus(s); setShowFilterMenu(false); }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            status === s ? "font-bold text-slate-900 bg-slate-50" : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#14123a] text-white text-xs uppercase tracking-wide">
                      <th className="text-left font-semibold px-6 py-4">Request ID</th>
                      <th className="text-left font-semibold px-6 py-4">Vehicle</th>
                      <th className="text-left font-semibold px-6 py-4">Part</th>
                      <th className="text-left font-semibold px-6 py-4">Status</th>
                      <th className="text-left font-semibold px-6 py-4">Quote</th>
                      <th className="text-left font-semibold px-6 py-4">Received</th>
                      <th className="text-left font-semibold px-6 py-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-slate-400 py-12">
                          {requests.length === 0
                            ? "This customer hasn't sent a request yet."
                            : "No requests match your filters."}
                        </td>
                      </tr>
                    )}
                    {pageRows.map((r) => (
                      <tr key={r._id} className="border-b border-gray-100 last:border-0 hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-800">{r.requestId}</div>
                          <div className="flex items-center gap-2 mt-1 text-slate-300">
                            {r.hasPhoto && <ImageIcon size={13} />}
                            {r.hasVoice && <Mic size={13} />}
                            {r.commentCount > 0 && (
                              <span className="flex items-center gap-0.5 text-xs text-slate-400">
                                <MessageCircle size={13} /> {r.commentCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-slate-700 font-semibold">{r.rego || r.vin || "—"}</div>
                          {(r.rego || r.vin) && (
                            <div className="text-xs text-slate-400">{r.idType || (r.rego ? "Rego" : "VIN")}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-slate-700 max-w-[220px] truncate">
                            {r.partRequired || r.notes || "—"}
                          </div>
                          {r.partNumber && (
                            <div className="text-xs text-slate-400">#{r.partNumber}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold whitespace-nowrap ${statusPill(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {r.quoteStatus && r.quoteStatus !== "none" ? (
                            <>
                              <div className="font-bold text-slate-800">{fmtMoney(r.quoteTotal)}</div>
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 mt-1 text-[10px] font-bold uppercase ${QUOTE_STYLES[r.quoteStatus]}`}>
                                {r.quoteStatus}
                              </span>
                            </>
                          ) : (
                            <span className="text-slate-400">Not quoted</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-slate-700 font-semibold">{fmtDate(r.createdAt)}</div>
                          <div className="text-xs text-slate-400">{fmtTime(r.createdAt)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => navigate(`/admin/requests/${r._id}`)}
                            className="border border-blue-200 text-blue-700 rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-blue-50 whitespace-nowrap"
                          >
                            View Request
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-6 py-4">
                <span className="text-sm text-slate-400">
                  {filtered.length === 0
                    ? "No requests"
                    : `Showing ${(page - 1) * PAGE_SIZE + 1} to ${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length} requests`}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 text-slate-400 disabled:opacity-40"
                  >
                    <ChevronLeft size={15} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 5).map((n) => (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`w-8 h-8 grid place-items-center rounded-lg text-sm font-semibold ${
                        n === page ? "bg-blue-700 text-white" : "text-slate-500 hover:bg-slate-50"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 grid place-items-center rounded-lg border border-gray-200 text-slate-400 disabled:opacity-40"
                  >
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <span className={`grid place-items-center w-10 h-10 rounded-xl ${tone}`}>
        <Icon size={18} />
      </span>
      <div className="mt-4 text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}